import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import { usePopper } from 'react-popper';
import eventBus from '@sas/nova-core/eventBus';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserName, getBrowserName } from '../../browser-name';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { usePopoverRoot } from '../../hooks/usePopoverRoot';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { useIsRTL } from '../../locale';
import { renderDesignTokens, useDesignTokens, useGlobalDesignTokens } from '../../theme';
import { getScrollbarSize } from '../../util/dom';
import { postfixTestId } from '../../util/testId';
import { useDirectSpeech } from '../DirectSpeech';
import { Heading } from '../Heading';
import { useModalA11YContext } from '../Modal/ModalA11YProvider';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { useOnOutsidePopoverMouseDown } from './__internal__/hooks/useOnOutsidePopoverMouseDown';
import { usePopoverManager } from './PopoverManager';
import cn from './Popover.css.json';
import './Popover.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('Popover');
export const publishPopoverEvent = eventName => {
  eventBus.publish(eventName);
};
const subscribePopoverEvent = (eventName, listener) => {
  eventBus.subscribe(eventName, listener);
};
const unsubscribePopoverEvent = (eventName, listener) => {
  eventBus.unsubscribe(eventName, listener);
};
export const Popover = /*#__PURE__*/forwardRef(function Popover({
  isOpen = true,
  ...restProps
}, ref) {
  return /*#__PURE__*/_jsx(AnimatePresence, {
    mode: "wait",
    children: isOpen ? /*#__PURE__*/_jsx(PopoverPortal, {
      ref: ref,
      isOpen: isOpen,
      ...restProps
    }) : null
  });
});
export const PopoverPortal = /*#__PURE__*/forwardRef(function PopoverPortal(props, ref) {
  const [containerElement, setContainerElement] = useState(null);
  const [popoverElement, setPopoverElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const [isFocusLockDisabled, setIsFocusLockDisabled] = useState(false);
  const isRTL = useIsRTL();
  const [titleId, contentId] = useHtmlId(2);
  // If position is passed in use this virtual element
  const virtualElement = useMemo(() => ({
    // DOMRect doesn't exist in jsdom so just create an object to represent one here
    getBoundingClientRect: () => ({
      x: 0,
      y: 0,
      height: 0,
      width: 0,
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      toJSON: () => {}
    })
  }), []);
  const [id, setId] = useState(undefined);
  const popoverManager = usePopoverManager();
  const placement = props.placement ?
  // if we have a top-start or bottom-start placement set and RTL is on, switch it to end
  isRTL && (props.placement === 'top-start' || props.placement === 'bottom-start') ? props.placement.replace('start', 'end') :
  // if we have a top-end or bottom-end placement set and RTL is on, switch it to start
  isRTL && (props.placement === 'top-end' || props.placement === 'bottom-end') ? props.placement.replace('end', 'start') :
  // otherwise use passed in placement
  props.placement :
  // if no placement was set, default to right or left depending on RTL
  isRTL ? 'left' : 'right';

  // See whether we're using a `referenceElementRef` to snap to, or use passed in `position` to position a virtual element to snap to instead
  let referenceElement;
  if (props.referenceElementRef?.current) {
    referenceElement = props.referenceElementRef?.current;
  } else if (props.position) {
    referenceElement = virtualElement;
  }
  const {
    styles,
    attributes,
    update,
    forceUpdate,
    state
  } = usePopper(referenceElement, popoverElement, {
    placement,
    modifiers: [{
      name: 'arrow',
      options: {
        element: arrowElement,
        padding: 5
      }
    }, {
      name: 'offset',
      options: {
        offset: [0, props.hideArrow ? 0 : 14]
      }
    }, {
      // Prevents an issue with the auto-start and auto-end placements
      name: 'flip',
      options: {
        flipVariations: false
      }
    }, {
      // Adds a little padding to the edge of the window
      name: 'preventOverflow',
      options: {
        padding: {
          top: 10,
          left: 0,
          bottom: 10,
          right: 0
        },
        boundary: props.popoverRoot ?? undefined
      }
    }]
  });
  const titleRef = useRef(null);
  const footerRef = useRef(null);
  const popoverPaneRef = useRef(null);
  const [popoverDimensions, setPopoverDimensions] = useState({
    maxHeight: undefined,
    maxWidth: undefined
  });
  const calculateDimensions = useCallback(popoverState => {
    if (!popoverState?.rects?.reference) return;
    const style = props.style;
    // Popper.js will automatically move things to the side with the most room based on placement
    // Need to restrict the bounds of the popover so it fits on the side with the largest space available if it would otherwise overflow the screen

    const windowHeight = props.popoverRoot ? props.popoverRoot.getBoundingClientRect().height : window.innerHeight;
    const windowWidth = props.popoverRoot ? props.popoverRoot.getBoundingClientRect().width : window.innerWidth;

    // We need to account for the width of the arrow if it's visible
    const arrowOffset = props.hideArrow ? 0 : 14;
    const placement = popoverState.placement;
    const orientation = placement.indexOf('top') !== -1 || placement.indexOf('bottom') !== -1 ? 'vertical' : placement.indexOf('left') !== -1 || placement.indexOf('right') !== -1 ? 'horizontal' : 'auto';

    // Popover looks like it might be cut off if extending all the way to the top and bottom of the screen, add a small buffer.
    // Horizontally oriented popovers will touch the top and bottom of the screen so they need double the buffer
    const bufferSize = orientation === 'vertical' ? 10 : 20;
    const rect = popoverState.rects.reference;
    const headerHeight = titleRef.current?.getBoundingClientRect().height ?? 0;
    const footerHeight = footerRef.current?.getBoundingClientRect().height ?? 0;
    // If using a custom root, we need to adjust calculations for how far down the page the custom root is
    const customRootHeightOffset = props.popoverRoot ? props.popoverRoot.getBoundingClientRect().top : 0;
    // This is the amount of vertical height available for the content area above and below the reference element
    const availableHeightForContentAboveReference = rect.y - bufferSize - arrowOffset - headerHeight - footerHeight - customRootHeightOffset;
    const availableHeightForContentBelowReference = windowHeight - rect.y - rect.height - bufferSize - arrowOffset - headerHeight - footerHeight + customRootHeightOffset;
    let availableHeight = windowHeight - bufferSize - headerHeight - footerHeight;

    // If using a custom root, we need to adjust calculations for how far right on the page the custom root is
    const customRootWidthOffset = props.popoverRoot ? props.popoverRoot.getBoundingClientRect().left : 0;
    const widthLeftOfReference = rect.x - arrowOffset - customRootWidthOffset;
    const widthRightOfReference = windowWidth - rect.x - rect.width - arrowOffset + customRootWidthOffset;
    let availableWidth = windowWidth;
    if (orientation === 'vertical') {
      // This is in a vertical orientation, limit popover height to largest space available above or below
      availableHeight = Math.max(availableHeightForContentBelowReference, availableHeightForContentAboveReference);
    } else if (orientation === 'horizontal') {
      // This is in a horizontal orientation, limit popover width to largest space available left or right
      availableWidth = Math.max(widthLeftOfReference, widthRightOfReference);
    } else if (orientation === 'auto') {
      // For auto placement size popover to be placed on whichever side has the most room
      if (Math.max(widthRightOfReference, widthLeftOfReference) >= Math.max(availableHeightForContentAboveReference, availableHeightForContentBelowReference)) {
        // There is more room horizontally, limit popover width to largest space available left or right
        availableWidth = Math.max(widthRightOfReference, widthLeftOfReference);
      } else {
        // There is more room vertically, limit popover height to largest space available above or below
        availableHeight = Math.max(availableHeightForContentAboveReference, availableHeightForContentBelowReference);
      }
    }

    // Max height and width only apply when smaller than the calculated maximums. Popover should never overflow outside of the screen.
    const styleMaxHeight = style?.maxHeight ?? '100vh';
    const styleMaxWidth = style?.maxWidth ?? '100vw';
    const popoverPaneMaxHeight = `min(${availableHeight}px, ${styleMaxHeight})`;
    const popoverPaneMaxWidth = `min(${availableWidth}px, ${styleMaxWidth})`;
    setPopoverDimensions({
      maxHeight: popoverPaneMaxHeight,
      maxWidth: popoverPaneMaxWidth
    });
    if (popoverPaneRef.current) {
      // quicker paint to reduce any flicker, if any, before next rendering
      if (typeof popoverPaneMaxHeight === 'string') popoverPaneRef.current.style.maxHeight = popoverPaneMaxHeight;
      if (typeof popoverPaneMaxWidth === 'string') popoverPaneRef.current.style.maxWidth = popoverPaneMaxWidth;
    }
  }, [props.popoverRoot, props.hideArrow, props.style]);
  useImperativeHandle(ref, () => ({
    update,
    forceUpdate,
    container: containerElement,
    state
  }));
  const [setRootRef] = useResizeObserverRef(() => {
    update?.();
    calculateDimensions(state);
  });
  if (props.popoverRoot) {
    setRootRef(props.popoverRoot);
  }

  // containerElement isn't defined until all dimension calculations have been run, do one last update of popover's position (Prevents issue in NOVACOMMONS-19528)
  useEffect(() => {
    if (containerElement) {
      update?.();
    }
  }, [containerElement, update]);

  // Refresh our position when RTL mode changes, the child elements change, or provided `position` changes
  useEffect(() => {
    if (props.position) {
      virtualElement.getBoundingClientRect = () => props.position;
    }
    update?.();
    calculateDimensions(state);
  }, [isRTL, props.children, props.position, update, virtualElement, state, calculateDimensions]);
  const dt = useDesignTokens();
  const globalDesignTokens = useGlobalDesignTokens();

  // In Firefox the scrollbar is applied inside the padding, causing it to overlay things, this adds extra padding to fix it when the scrollbar is visible
  const [paddingInlineEnd, setPaddingInlineEnd] = useState(undefined);
  useEffect(() => {
    if (containerElement && paddingInlineEnd === undefined) {
      const currentPaddingInlineEnd = window.getComputedStyle(containerElement).getPropertyValue('padding-inline-end');
      if (getBrowserName() === BrowserName.Firefox && containerElement?.scrollHeight > containerElement.offsetHeight) {
        const offset = -containerElement.offsetWidth + containerElement.scrollWidth + getScrollbarSize();
        offset > 0 ? setPaddingInlineEnd(`calc(${currentPaddingInlineEnd} + ${offset}px)`) : setPaddingInlineEnd(currentPaddingInlineEnd);
      } else {
        setPaddingInlineEnd(currentPaddingInlineEnd);
      }
    }
  }, [containerElement, paddingInlineEnd]);
  const defaultPopoverRoot = usePopoverRoot();
  const popoverRoot = props.popoverRoot ?? defaultPopoverRoot;

  // This closes on unmount and when onOpen changes to false to handle both onOpen and conditional rendering. Make sure close only gets triggered once.
  const closeTriggered = useRef(false);

  // Tell PopoverManager when this popover opens so that it can assign it a key/group/level, and set the id it returns here
  useEffect(() => {
    if (id === -1) {
      for (const popover of popoverManager.activePopovers.values()) {
        if (popover.referenceElementRef === props.referenceElementRef && popover.popoverElement === popoverElement) {
          return setId(popover.id);
        }
      }
    }

    // Should only do this once to get back an id
    if (!popoverElement || id !== undefined) return;
    const returnId = popoverManager.openPopover({
      popoverElement,
      referenceElementRef: props.referenceElementRef
    });
    closeTriggered.current = false;

    // PopoverManager will return the id of this popover
    setId(returnId);
  }, [props.referenceElementRef, props.position, id, popoverElement, popoverManager]);
  const cleanupPopover = id => {
    setId(undefined);
    closeTriggered.current = true;
    popoverManagerRef.current.closePopover(id);
    setContainerElement(null);
    setPopoverDimensions({
      maxHeight: undefined,
      maxWidth: undefined
    });
    setPaddingInlineEnd(undefined);
  };

  // Tell PopoverManager when this popover closes
  // We only want this happening on dismount, not every time values coming from PopoverManager change, so this ref is used to keep popoverManger out of the dependency array
  const popoverManagerRef = useRef(popoverManager);
  popoverManagerRef.current = popoverManager;
  useEffect(() => {
    return () => {
      if (id !== undefined && id > -1 && popoverManagerRef.current && !closeTriggered.current) {
        cleanupPopover(id);
      }
    };
  }, [id]);

  // Also want to notify PopoverManager when isOpen becomes false
  useEffect(() => {
    if (id !== undefined && id > -1 && popoverManagerRef.current && !closeTriggered.current && props.isOpen === false) {
      cleanupPopover(id);
    }
  }, [id, props.isOpen]);

  // This checks if a mouse click fell outside of our popover content, or the content of any of its child popovers
  useOnOutsidePopoverMouseDown(props.isModal ? undefined : () => {
    props.onClose?.();
    props.onOutsideClick?.();
  }, popoverElement, props.referenceElementRef?.current, popoverManager.activePopovers, id);
  const onClose = props.onClose;
  // Used to close all instances of popover when resize or scroll occurs
  const onClosePopover = useCallback(event => {
    const target = event.target;
    // Don't do anything if popover is closed, `onClose` is unhandled, or this is a modal popover
    if (props.isOpen === false || !onClose || props.isModal) return;
    if (event instanceof MouseEvent || event instanceof WheelEvent) {
      const allMenuContent = document.getElementsByClassName(cn.popover);
      for (const content of allMenuContent) {
        if (content && content.contains(target)) {
          return;
        }
      }
    }
    onClose();
  }, [onClose, props.isOpen, props.isModal]);
  useEffect(() => {
    // Listen to the resize action to close the popover.
    window.addEventListener('resize', onClosePopover);
    // Listen to the wheel action to close the popover.
    window.addEventListener('wheel', onClosePopover);
    // this line comes from a basic scroll hook called useDisableScroll.
    // The relevant stackoverflow(https://stackoverflow.com/questions/4770025/how-to-disable-scrolling-temporarily)
    // giving the possible cause of the above line is that 'This code will work on Chrome 56 and further'.
    document.addEventListener('wheel', onClosePopover);
    window.addEventListener('blur', onClosePopover);
    return () => {
      window.removeEventListener('resize', onClosePopover);
      window.removeEventListener('wheel', onClosePopover);
      document.removeEventListener('wheel', onClosePopover);
      window.removeEventListener('blur', onClosePopover);
    };
  }, [onClosePopover]);
  useEffect(() => {
    const handleGlobalKeyDown = event => {
      if (props.isOpen && event.key === 'F10' && event.shiftKey) {
        event.preventDefault();
      }
    };
    if (props.isOpen) {
      window.addEventListener('keydown', handleGlobalKeyDown, true);
    }
    return () => window.removeEventListener('keydown', handleGlobalKeyDown, true);
  }, [props.isOpen]);
  const directSpeech = useDirectSpeech();
  const directSpeechAssertive = useDirectSpeech({
    mode: 'assertive'
  });

  // handle custom event handlers
  useEffect(() => {
    if (!props.isModal && props.isOpen !== false) {
      const disableFocusLockNonModal = () => {
        setIsFocusLockDisabled(true);
      };
      const closePopover = () => {
        onClose?.();
      };
      subscribePopoverEvent('popover:disableFocusLockNonModal', disableFocusLockNonModal);
      subscribePopoverEvent('popover:onCloseNonModal', closePopover);
      return () => {
        unsubscribePopoverEvent('popover:disableFocusLockNonModal', disableFocusLockNonModal);
        unsubscribePopoverEvent('popover:onCloseNonModal', closePopover);
      };
    } else if (props.isModal) {
      directSpeech?.notify('');
      directSpeechAssertive?.notify('');
    }
  }, [props.isModal, props.isOpen, onClose, directSpeech, directSpeechAssertive]);

  // Applies aria-hidden to an element that wraps the base application ui layer. Prevents Accessibility issues
  const publishOpenState = useModalA11YContext();
  useEffect(() => {
    if (props.isOpen && props.isModal) {
      publishOpenState(true);
      return () => {
        publishOpenState(false);
      };
    }
  }, [props.isOpen, publishOpenState, props.isModal]);
  const popperPlacement = attributes.popper?.['data-popper-placement'] ?? '';
  const hiddenPosition = {
    x: 0,
    y: 0
  };
  if (popperPlacement.startsWith('top')) {
    hiddenPosition.y = 16;
  } else if (popperPlacement.startsWith('bottom')) {
    hiddenPosition.y = -16;
  } else if (popperPlacement.startsWith('right')) {
    hiddenPosition.x = -16;
  } else if (popperPlacement.startsWith('left')) {
    hiddenPosition.x = 16;
  }
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const popoverAnimation = !shouldPlayAnimation ? undefined : {
    closed: {
      opacity: 0,
      ...hiddenPosition
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      ...hiddenPosition,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    }
  };
  const content =
  /*#__PURE__*/
  /* By default this container will use the title id for aria-labelledby */
  _jsx("div", {
    id: props.id,
    "data-testid": props.testId,
    ref: setPopoverElement,
    style: {
      ...styles.popper,
      ...getPopoverDesignTokens(dt, {
        font: props.font,
        theme: props.theme
      }),
      ...props.futureTheme?.(dt),
      /*
        If the popover is placed at the bottom(error message in input fields) and the animation is disabled, 
        we need to add a margin to the top of the popover to prevent it from overlapping with the bottom border of reference element (input container).
        This is only necessary when the animation is disabled because, when enabled, the animation will add proper spacing between the popover and the reference element.
      */
      marginBlockStart: popperPlacement.startsWith('bottom') && !shouldPlayAnimation ? dt.tokens['space-block-xxs'] : undefined
    },
    ...attributes.popper,
    // popover-hide class is used to hide the popover when its reference element is hidden, so don't apply it if the corresponding prop isn't set
    className: cx(cn.popoverOuter, props.hideOnRefHidden ? cn.popoverHide : undefined),
    role: props.role,
    "aria-label": props.ariaLabel,
    "aria-labelledby": props.ariaLabelledby ?? (props.title ? titleId : undefined),
    "aria-describedby": props.ariaDescribedby ?? (props.focusable ? contentId : undefined),
    tabIndex: props.focusable ? 0 : undefined,
    onKeyDown: e => {
      setIsFocusLockDisabled(false);
      if (e.key === 'Escape') {
        props.onClose?.();
        props.onEscape?.();
        // This prevents an issue where closing a popover nested inside another component that closes on Escape, such as Settings or PropertyPaneLayout, would close both
        e.stopPropagation();
      }
      if (e.key === ' ') {
        // This prevents an issue where the space button would toggle checkboxes outside of this popover (Checkbox: WithLabel story was an example of this when using the HelpPopup)
        e.stopPropagation();
      }
      if (popoverPaneRef.current && popoverPaneRef.current.scrollBy && document.activeElement === popoverElement) {
        if (e.key === 'ArrowUp') {
          popoverPaneRef.current.scrollBy({
            top: -50,
            left: 0,
            behavior: 'auto'
          });
          e.stopPropagation();
        }
        if (e.key === 'ArrowDown') {
          popoverPaneRef.current.scrollBy({
            top: 50,
            left: 0,
            behavior: 'auto'
          });
          e.stopPropagation();
        }
        if (e.key === 'ArrowLeft') {
          popoverPaneRef.current.scrollBy({
            top: 0,
            left: -50,
            behavior: 'auto'
          });
          e.stopPropagation();
        }
        if (e.key === 'ArrowRight') {
          popoverPaneRef.current.scrollBy({
            top: 0,
            left: 50,
            behavior: 'auto'
          });
          e.stopPropagation();
        }
      }
    },
    onClick: e => e.stopPropagation(),
    ...(!props.isModal && {
      onMouseDown: () => setIsFocusLockDisabled(false)
    }),
    onContextMenu: e => {
      if (!props.allowOnContextMenuPropagation) {
        e.stopPropagation();
      }
    },
    children: /*#__PURE__*/_jsxs(motion.div, {
      variants: popoverAnimation,
      initial: "closed",
      animate: "open",
      exit: "exit",
      ...(shouldPlayAnimation ? props.animation : null),
      className: cn.popover,
      children: [/*#__PURE__*/_jsxs("div", {
        "data-landmark-label": !props.onClose && !props.isModal ? props.landmarkLabel || props.title : undefined,
        className: cx(cn.popoverInner, props.innerClassName),
        onContextMenu: e => props.disableContextMenu ? e.preventDefault() : undefined,
        children: [props.title ? /*#__PURE__*/_jsx(Heading, {
          id: titleId,
          testId: postfixTestId(props.testId, 'title'),
          level: props.isModal ? 1 : 2,
          style: props.titleStyle,
          className: cx(cn.popoverTitle, props.titleClassName),
          ref: titleRef,
          children: props.title
        }) : null, /*#__PURE__*/_jsx("div", {
          id: contentId,
          "data-testid": postfixTestId(props.testId, 'content'),
          className: cx(cn.popoverPane, props.className, !!props.title && cn.hasTitle, !!props.footer && cn.hasFooter),
          style: {
            ...props.style,
            maxHeight: popoverDimensions.maxHeight,
            maxWidth: popoverDimensions.maxWidth,
            paddingInlineEnd
          },
          ref: ref => {
            if (ref && popoverDimensions.maxHeight === undefined) {
              calculateDimensions(state);
            }
            // Wait for dimension calculation so any parents get correct size of this popover
            if (ref && state && popoverDimensions.maxHeight !== undefined) {
              setContainerElement(ref);
            }
            popoverPaneRef.current = ref;
          },
          children: props.children
        }), props.footer ? /*#__PURE__*/_jsx("div", {
          ref: footerRef,
          "data-testid": postfixTestId(props.testId, 'footer'),
          className: cn.popoverFooter,
          children: props.footer
        }) : null]
      }), props.hideArrow ? null : /*#__PURE__*/_jsx("div", {
        ...attributes.arrow,
        "data-testid": postfixTestId(props.testId, 'arrow'),
        ref: setArrowElement,
        style: {
          ...getPopoverDesignTokens(dt, {
            font: props.font,
            theme: props.theme
          }),
          ...styles.arrow
        },
        className: cn.arrow
      })]
    })
  });
  if (popoverRoot === null || props.isOpen === false ||
  // Popover can't render if neither a `referenceElementRef` or `position` are provided
  !props.referenceElementRef && !props.position) {
    return null;
  }
  return /*#__PURE__*/createPortal( /*#__PURE__*/_jsx("div", {
    className: cx(cn.viewportOverlay, props.overlayClassName, !props.isModal ? cn.blockPointerEvents : null),
    style: globalDesignTokens,
    onMouseDown: event => {
      if (props.isModal && event.currentTarget === event.target) {
        // prevent focus change when the overlay is clicked
        event.preventDefault();
      }
    },
    children: /*#__PURE__*/_jsx(FocusLock, {
      ...(!props.isModal && {
        disabled: isFocusLockDisabled
      }),
      returnFocus: props.returnFocus !== false,
      ...props.focusLockProps,
      children: content
    })
  }), popoverRoot);
});
export const getPopoverDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      bg: dt.tokens['color-container-background-0'],
      fg: dt.tokens['color-text-neutral'],
      sh: dt.tokens['shadow-depth-m'],
      bd: dt.tokens['color-container-border-4'],
      padding: dt.tokens['space-base-m'],
      'border-radius': dt.tokens['size-border-radius-xxs'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'title-font-weight': dt.tokens['font-style-weight-700'],
      'title-fg': dt.tokens['color-heading-text'],
      'title-font-family': opts?.font?.fontFamily ?? dt.tokens['font-family-secondary'],
      ...opts?.font,
      ...opts?.theme?.()
    }, 'popover'),
    ...renderDesignTokens({
      bg: dt.tokens['color-container-background-0'],
      bd: dt.tokens['color-container-border-4']
    }, 'popover-arrow'),
    ...(!dt.enableNovaTokens && getPopoverLegacyDesignTokens(dt, opts))
  };
};
export const getPopoverLegacyDesignTokens = (dt, opts) => {
  let arrowtokens;
  switch (dt.themeVariant) {
    case 'dark':
      arrowtokens = {
        bd: dt.c.p2
      };
    case 'high-contrast':
      arrowtokens = {
        bd: dt.c.white
      };
    default:
      arrowtokens = {
        bd: dt.c.n3
      };
  }
  return {
    ...renderDesignTokens({
      ...dt.reportTokens.backgroundsDepth.medium,
      bd: dt.reportTokens.stateStandard.normal.bd,
      'font-size-m': dt.reportTokens.font.size.m,
      'title-font-weight': dt.reportTokens.font.weight.thick,
      'title-fg': dt.c.primary,
      'title-fontFamily': opts?.font?.fontFamily ?? dt.reportTokens.font.family,
      ...opts?.font,
      ...opts?.theme?.()
    }, 'popover'),
    ...renderDesignTokens(arrowtokens, 'popover-arrow')
  };
};
//# sourceMappingURL=Popover.js.map